'use client';

import { CartButton, CartButtonProps } from 'mmds';
import Toolbar from '@app/components/ui/toolbar/Toolbar';
import ProductBuy from '../product-buy/product-buy';
import useHorizontalScrollObserver from '@app/lib/hooks/use-horizontal-scroll-observer';
import useUI from '@app/components/ui/state';
import s from './product-toolbar.module.css';
import cn from 'clsx';
import TitleAndVariant from './title-and-variant';
import { useBuyContext } from '@app/[lang]/products/context';
import { VariantLifecycleStatus } from '@app/[lang]/products/utilities/get-product-lifecycle-status';

const ProductToolbar = ({
    title,
    sizes,
    price,
    compareAtPrice,
    currencyCode,
    variantName,
}) => {
    const {
        scrollContainerRef: desktopScrollContainerRef,
        hasReachedEnd: desktopHasReachedEnd,
        hasOverflowRight: desktopHasOverflowRight,
    } = useHorizontalScrollObserver();
    const {
        scrollContainerRef: mobileScrollContainerRef,
        hasReachedEnd: mobileHasReachedEnd,
        hasOverflowRight: mobileHasOverflowRight,
    } = useHorizontalScrollObserver();
    const productHeroRef = useUI((state) => state.productHeroRef);

    const scrollToProductHeroBottom = () => {
        if (productHeroRef.current) {
            const galleryBottom =
                productHeroRef.current.getBoundingClientRect().bottom +
                window.scrollY -
                280;
            window.scrollTo({
                top: galleryBottom,
                behavior: 'smooth',
            });
        }
    };

    // Apply gradient mask effect when scroll container has overflow
    // and the end of the scroll container has not been reached.
    const gradientMask =
        !mobileHasReachedEnd || !desktopHasReachedEnd
            ? cn(s.gradientMask, s.active)
            : s.gradientMask;

    const {
        selectedVariant,
        isVariantInCheckout,
        variantLifecycleStatus,
        callbacks,
    } = useBuyContext();

    const button: CartButtonProps = {
        label: 'Select Size',
        state: 'default',
        onClick: () => scrollToProductHeroBottom(),
    };

    if (selectedVariant) {
        if (variantLifecycleStatus === VariantLifecycleStatus.ACTIVE) {
            button.label = 'Add';
            button.state = 'selected';
            button.onClick = callbacks.addToCart;
        }

        if (variantLifecycleStatus === VariantLifecycleStatus.NOTIFY) {
            button.label = 'Notify';
            button.state = 'alternative';
            button.onClick = () => scrollToProductHeroBottom();
        }

        if (variantLifecycleStatus === VariantLifecycleStatus.SOLD_OUT) {
            button.label = 'Sold out';
            button.state = 'soldOut';
            button.onClick = () => scrollToProductHeroBottom();
        }
    }

    if (isVariantInCheckout) {
        button.label = 'Added';
        button.state = 'added';
        button.onClick = () => scrollToProductHeroBottom();
    }

    return (
        <Toolbar
            shouldStick
            position="top"
            targetRef={productHeroRef}
            initiallyHidden={true}
            positioningMode="fixed"
        >
            <div className={cn(s.wrapper, 'hidden-on-mobile')}>
                <div
                    className={cn(s.scrollContainer, s.spacer)}
                    ref={desktopScrollContainerRef}
                >
                    <div className={cn(s.wrapperInner)}>
                        <TitleAndVariant
                            title={title}
                            variantName={variantName}
                        />
                    </div>
                </div>
                <div className={cn(gradientMask)}>
                    <ProductBuy
                        price={price}
                        compareAtPrice={compareAtPrice}
                        sizes={sizes}
                        currencyCode={currencyCode}
                    />
                </div>
            </div>
            <div className={cn(s.wrapper, 'hidden-on-desktop')}>
                <div
                    className={cn(s.scrollContainer, s.spacer)}
                    ref={mobileScrollContainerRef}
                >
                    <TitleAndVariant title={title} variantName={variantName} />
                </div>
                <div className={cn(gradientMask)}>
                    <CartButton
                        className={
                            button.label === 'Select Size' ? '' : s.cartButton
                        }
                        {...button}
                    />
                </div>
            </div>
        </Toolbar>
    );
};

export default ProductToolbar;
